import React, { useState, useEffect, useCallback } from 'react';
import Tabs from './Tabs';
import OrderList from './OrderList';
import SearchOrders from './SearchOders';
import { GetOrders, GetOrderStatus } from '../Data.js';

// Helper function to get status color
const getStatusColor = (status) => {
  const statusLower = status.toLowerCase();
  if (statusLower.includes('processing') || statusLower.includes('being processed')) {
    return 'bg-blue-100 text-blue-800';
  } else if (statusLower.includes('pending') || statusLower.includes('awaiting')) {
    return 'bg-yellow-100 text-yellow-800';
  } else if (statusLower.includes('confirmed') || statusLower.includes('payment confirmed')) {
    return 'bg-green-100 text-green-800';
  } else if (statusLower.includes('shipped') || statusLower.includes('transit')) {
    return 'bg-purple-100 text-purple-800';
  } else if (statusLower.includes('delivered') || statusLower.includes('completed')) {
    return 'bg-green-100 text-green-800';
  } else if (statusLower.includes('cancelled')) {
    return 'bg-red-100 text-red-800';
  } else if (statusLower.includes('returned')) {
    return 'bg-orange-100 text-orange-800';
  } else if (statusLower.includes('refunded')) {
    return 'bg-purple-100 text-purple-800';
  }
  return 'bg-gray-100 text-gray-800';
};

const StatusBadge = ({ status }) => {
  if (!status) return null;

  const colorClass = getStatusColor(status);
  
  return (
    <span 
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${colorClass} transition-colors duration-200`}
    >
      {status}
    </span>
  );
};

const ReturnsAndOrdersPage = () => {
  const [selectedTab, setSelectedTab] = useState('Orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('3 months');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('All');

  const tabs = ['Orders', 'Buy Again', 'Not Yet Shipped', 'Cancelled Orders', 'Returned Orders', 'Delivered Orders'];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get user ID from localStorage
      const userId = localStorage.getItem("userID");
      if (!userId) {
        throw new Error("User ID not found. Please log in again.");
      }

      // Fetch orders and statuses using Data.js functions
      const [ordersData, statusesData] = await Promise.all([
        GetOrders(userId),
        GetOrderStatus()
      ]);
      
      let filteredData = Array.isArray(ordersData) ? ordersData : [];
      
      // Filter based on selected tab
      if (selectedTab === 'Buy Again') {
        filteredData = ordersData.filter(order => 
          order.status.toLowerCase().includes('delivered') || 
          order.status.toLowerCase().includes('completed')
        );
      } else if (selectedTab === 'Not Yet Shipped') {
        filteredData = ordersData.filter(order => 
          order.status.toLowerCase().includes('processing') ||
          order.status.toLowerCase().includes('pending') ||
          order.status.toLowerCase().includes('confirmed')
        );
      } else if (selectedTab === 'Cancelled Orders') {
        filteredData = ordersData.filter(order => 
          order.status.toLowerCase().includes('cancelled')
        );
      } else if (selectedTab === 'Returned Orders') {
        filteredData = ordersData.filter(order => 
          order.status.toLowerCase().includes('returned')
        );
      } else if (selectedTab === 'Delivered Orders') {
        filteredData = ordersData.filter(order => 
          order.status.toLowerCase().includes('delivered') ||
          order.status.toLowerCase().includes('completed')
        );
      }
      
      setOrders(filteredData);
      setFilteredOrders(filteredData);
      setOrderStatuses(statusesData);
    } catch (err) {
      setError('Failed to fetch orders. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedTab, timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = useCallback((query) => {
    applyFilters(query, selectedStatus);
  }, [orders, selectedStatus]);
  
  const handleStatusFilter = useCallback((status) => {
    setSelectedStatus(status);
    applyFilters('', status);
  }, [orders]);
  
  const applyFilters = useCallback((searchQuery, statusFilter) => {
    let filtered = [...orders];
    
    // Apply search filter
    if (searchQuery && searchQuery.trim()) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(order => 
        order.orderID.toLowerCase().includes(lowerCaseQuery) ||
        order.status.toLowerCase().includes(lowerCaseQuery) ||
        (order.products && order.products.some(product => 
          product.productName.toLowerCase().includes(lowerCaseQuery)
        ))
      );
    }
    
    // Apply status filter
    if (statusFilter && statusFilter !== 'All') {
      filtered = filtered.filter(order => 
        order.status.toLowerCase().includes(statusFilter.toLowerCase())
      );
    }
    
    setFilteredOrders(filtered);
  }, [orders]);

  const handleTimeRangeChange = useCallback((newRange) => {
    setTimeRange(newRange);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8 font-sans">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Your Orders</h1>
        
        {/* Tabs with horizontal scroll on mobile */}
        <div className="overflow-x-auto mb-4 sm:mb-6">
          <div className="border-b border-gray-200 min-w-max sm:min-w-0">
            <Tabs 
              tabs={tabs} 
              selectedTab={selectedTab} 
              onSelect={setSelectedTab} 
              className="flex space-x-2 sm:space-x-6"
            />
          </div>
        </div>

        {/* Filters and search - stacked on mobile */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative w-full sm:w-48">
              <select
                id="timeRange"
                className="block w-full bg-white border border-gray-300 text-gray-700 py-2 px-3 pr-8 rounded text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                value={timeRange}
                onChange={(e) => handleTimeRangeChange(e.target.value)}
              >
                <option value="3 months">Past 3 months</option>
                <option value="6 months">Past 6 months</option>
                <option value="1 year">Past year</option>
                <option value="2025">2025</option>
              </select>
            </div>
            
            <div className="relative w-full sm:w-48">
              <select
                id="statusFilter"
                className="block w-full bg-white border border-gray-300 text-gray-700 py-2 px-3 pr-8 rounded text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                value={selectedStatus}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                <option value="All">All Statuses</option>
                {orderStatuses.map(status => (
                  <option key={status.statusID} value={status.status}>
                    {status.status}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <SearchOrders 
            onSearch={handleSearch} 
            className="w-full sm:w-64"
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8 sm:py-12">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-t-2 border-b-2 border-yellow-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 mb-4 sm:mb-6 text-sm sm:text-base">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p>{error}</p>
            </div>
          </div>
        ) : (
          <>
            {filteredOrders.length > 0 ? (
              <OrderList orders={filteredOrders} StatusBadge={StatusBadge} />
            ) : (
              <div className="text-center py-8 sm:py-12">
                <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-2 text-base sm:text-lg font-medium text-gray-900">No {selectedTab} available</h3>
                <p className="mt-1 text-xs sm:text-sm text-gray-500">
                  {selectedTab === 'Buy Again' ? 
                    "You don't have any items available to buy again." :
                    `You don't have any ${selectedTab.toLowerCase()} in the past ${timeRange}.`
                  }
                </p>
                {timeRange === '3 months' && (
                  <button 
                    onClick={() => setTimeRange('2025')}
                    className="mt-3 px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-xs sm:text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    View orders in 2025
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(ReturnsAndOrdersPage);